import Product from './Product';
import {
  cartApi,
  getTranslation,
  openModal,
  showQuickshop,
  updateCart,
} from '../../helpers';

const selectors = {
  quickShopTrigger: '[data-quickshop-trigger]',
  modalProduct: '[data-product]',
  modalContent: 'quickshop-content',
  addToCartButton: '[data-add-to-cart-item]',
  addToCartText: '[data-add-to-cart-text]',
};

const MODAL_ID = 'quickshop';
const BUTTON_RESET_DELAY = 2000; // milliseconds

/**
 * A single product item in a collection / list of products
 *
 * @export
 * @class ProductItem
 */
export default class ProductItem {
  /**
   * Creates an instance of ProductItem.
   * @param {HTMLElement} el
   */
  constructor(el) {
    this.el = el;
    this.originalButtonText = null;

    this._initQuickShop();
    this._initAddToCart();
  }

  _initModal(data, quickShopUrl) {
    const quickshopHtml = showQuickshop(data);
    const quickShopContent = document.getElementById(selectors.modalContent);
    quickShopContent.innerHTML = quickshopHtml;

    openModal(MODAL_ID);

    const product = quickShopContent.querySelector(selectors.modalProduct);

    // Create a new Product instance for the requested quickshop product
    new Product(product, {
      isQuickShop: true,
      quickShopUrl,
      modalId: MODAL_ID,
    });
  }

  /**
   * Bind quickshop to a trigger
   */
  _initQuickShop() {
    const trigger = this.el.querySelector(selectors.quickShopTrigger);

    if (!trigger) {
      return;
    }

    trigger.addEventListener('click', () => {
      const { productUrl } = this.el.dataset;

      const quickShopUrl = `${productUrl}?section_id=${window.quickshopSectionId}`;

      fetch(quickShopUrl)
        .then(response => response.text())
        .then(data => {
          this._initModal(data, quickShopUrl);
        });
    });
  }

  /**
   * Initialize add to cart functionality
   */
  _initAddToCart() {
    const button = this.el.querySelector(selectors.addToCartButton);

    if (!button) {
      return;
    }

    // Store the original button text (including price) for later restoration
    const textElement = button.querySelector(selectors.addToCartText);
    if (textElement) {
      this.originalButtonText = textElement.textContent.trim();
    }

    button.addEventListener('click', this._onAddToCart);
  }

  /**
   * Handle add to cart button click
   */
  _onAddToCart = e => {
    e.preventDefault();

    const button = e.currentTarget;
    const variantId = this.el.dataset.variantId;

    if (!variantId || button.disabled) {
      return;
    }

    this._toggleLoading(button, true, getTranslation('products.adding'));

    cartApi
      .add({
        items: [
          {
            id: parseInt(variantId, 10),
            quantity: 1,
          },
        ],
      })
      .then(response => {
        this._toggleLoading(button, false, getTranslation('products.added'));

        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }

        // Reset button text after a short delay
        setTimeout(() => {
          if (button && !button.disabled && this.originalButtonText) {
            button.querySelector(selectors.addToCartText).textContent =
              this.originalButtonText;
          }
        }, BUTTON_RESET_DELAY);
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(error);
        this._toggleLoading(button, false, getTranslation('products.error'));

        // Reset button text after error
        setTimeout(() => {
          if (button && !button.disabled && this.originalButtonText) {
            button.querySelector(selectors.addToCartText).textContent =
              this.originalButtonText;
          }
        }, BUTTON_RESET_DELAY);
      });
  };

  /**
   * Toggle loading state of add to cart button
   */
  _toggleLoading(button, isLoading, text) {
    if (!button) {
      return;
    }

    button.disabled = isLoading;
    if (text) {
      button.querySelector(selectors.addToCartText).textContent = text;
    }
  }

  /**
   * Clean up event listeners
   */
  onUnload() {
    const button = this.el.querySelector(selectors.addToCartButton);
    if (button) {
      button.removeEventListener('click', this._onAddToCart);
    }
  }
}
